import {createFileRoute} from '@tanstack/react-router';

import {parseNonEmpty, parsePage} from '@/lib/search-params';
import type {UsersStatusFilter} from '@/modules/admin-users/components/UsersFilterRow';
import {UserListPage} from '@/modules/admin-users/pages/UserListPage';

/**
 * URL state for the admin users index. All fields are optional — links
 * from the settings nav land here with no params; the validator returns
 * concrete values the page can rely on (`status ?? "all"`, `page ?? 1`).
 *
 * Access is gated centrally: `_authenticated`'s `beforeLoad` +
 * runtime guard run `canAccessRoute(pathname, ...)` against
 * `ROUTE_REQUIREMENTS`, where `/admin/users**` requires `users:view`.
 */
export interface AdminUsersListSearch {
  status?: UsersStatusFilter;
  role?: string;
  /** Free-text search over name + email, matched server-side (the columns are
   *  encrypted, so the API decrypts and filters — see `list_users`). */
  q?: string;
  page?: number;
}

const STATUS_VALUES: ReadonlyArray<UsersStatusFilter> = ['active', 'inactive'];

function parseStatus(value: unknown): UsersStatusFilter | undefined {
  // Drop the "all" default so it never appears in the URL.
  return STATUS_VALUES.find((s) => s === value);
}

const validateAdminUsersListSearch = (search: Record<string, unknown>): AdminUsersListSearch => ({
  status: parseStatus(search.status),
  role: parseNonEmpty(search.role),
  q: parseNonEmpty(search.q),
  page: parsePage(search.page),
});

export const Route = createFileRoute('/_authenticated/admin/users/')({
  validateSearch: validateAdminUsersListSearch,
  component: UserListPage,
});
